import {
  hiddenGemsResult,
  ideationBriefResult,
  ideationRun,
  storyboardResult,
  videoResult,
} from '../../ideation/ideationFixtures.js';
import { briefResult, creative, run as iterationRun } from '../../storyFixtures.js';
import type { WorkflowRun } from '../../workflows.types.js';
import type { AlisonActionDisplay, AlisonSession, AlisonStep } from './alisonRun.js';

// TODO: drop with mockSession — real timestamps come from workflows.getSession
const MOCK_STARTED_AT = Date.parse('2026-07-28T12:00:00.000Z');
const MOCK_STEP_MS = 60_000;

// TODO: replace with workflows.getSession step summaries/outcomes; alisonCall is a proposed per-step field
export const MOCK_STEP_RESULTS: Record<string, MockStepResult> = {
  getHiddenGems: {
    summary: '148 live creatives scanned · 5 hidden gems found',
    itemCount: 5,
    outcomeKind: 'report',
    alisonCall: null,
    run: ideationRun(hiddenGemsResult, 'element_list', 'getHiddenGems'),
  },
  generateIdeationBrief: {
    summary: '3 elements — hooks, angle, KPI',
    itemCount: 1,
    outcomeKind: 'brief',
    alisonCall: "Alison's using all 5 gems — one brief built around the strongest angle.",
    run: ideationRun(ideationBriefResult, 'brief'),
  },
  updateIdeationBrief: {
    summary: '5 gems kept in the brief',
    itemCount: 1,
    outcomeKind: 'brief',
    alisonCall: "Alison's using all 5 gems — one brief built around the strongest angle.",
    run: ideationRun(ideationBriefResult, 'brief'),
  },
  generateStoryboard: {
    summary: '3 versions — top performer · top spender · net-new',
    itemCount: 3,
    outcomeKind: 'brief',
    alisonCall:
      "Alison's building 3 versions — from your top performer, your top spender, and a net-new direction.",
    run: ideationRun(storyboardResult, 'storyboard', 'generateStoryboard'),
  },
  generateVideo: {
    summary: '2 creatives rendered · 15-sec',
    itemCount: 2,
    outcomeKind: 'video',
    alisonCall: "Alison's cutting 3 creatives — one per storyboard version.",
    run: ideationRun(videoResult, 'video', 'generateVideo'),
  },
  getFatigue: {
    summary: '12 creatives losing efficiency',
    itemCount: 12,
    outcomeKind: 'report',
    alisonCall: null,
    run: ideationRun(
      { result_type: 'fatigue', creatives: [creative] },
      'creative_list',
      'getFatigue',
    ),
  },
  generateIterations: {
    summary: '18 recommendations across 6 creatives',
    itemCount: 6,
    outcomeKind: null,
    alisonCall: "Alison's picking every flagged creative — drop any before the brief.",
    run: iterationRun,
  },
  createBrief: {
    summary: '6 briefs, one per creative',
    itemCount: 6,
    outcomeKind: 'brief',
    alisonCall: null,
    run: ideationRun(briefResult, 'creative_list', 'createBrief'),
  },
};

export interface MockStepResult {
  summary: string;
  itemCount: number;
  outcomeKind: string | null;
  alisonCall: string | null;
  run: WorkflowRun;
}

function mockStep(
  actionType: string,
  index: number,
  status: AlisonStep['status'],
  displays: Record<string, AlisonActionDisplay>,
): AlisonStep {
  const result = MOCK_STEP_RESULTS[actionType];
  const name = displays[actionType]?.title ?? actionType;
  const completed = status === 'completed';
  return {
    runId: `mock-${actionType}`,
    triggeredByRunId: null,
    actionType,
    status,
    startedAt: new Date(MOCK_STARTED_AT + index * MOCK_STEP_MS).toISOString(),
    completedAt: completed
      ? new Date(MOCK_STARTED_AT + (index + 1) * MOCK_STEP_MS).toISOString()
      : null,
    summary: completed ? (result?.summary ?? null) : null,
    itemCount: completed ? (result?.itemCount ?? null) : null,
    error: null,
    decision: null,
    outcomeKind: result?.outcomeKind ?? null,
    params: {},
    actionName: name,
    configTitle: name,
    configurable: true,
    resultTitle: name,
    outcomeTitle: name,
    alisonCall: result?.alisonCall ?? null,
  };
}

// TODO: replace with workflows.getSession polling once the actions service runs the chain server-side
export function mockSession(
  path: string[],
  stopAt: number,
  tick: number,
  displays: Record<string, AlisonActionDisplay>,
): AlisonSession {
  const planned = path.slice(0, stopAt + 1);
  const completedCount = Math.min(tick, planned.length);
  const steps = planned
    .slice(0, completedCount)
    .map((actionType, index) => mockStep(actionType, index, 'completed', displays));
  const next = planned[completedCount];
  if (next !== undefined) steps.push(mockStep(next, completedCount, 'running', displays));
  const finished = completedCount === planned.length;
  return {
    rootRunId: `mock-${path[0] ?? 'run'}`,
    status: finished
      ? planned.length === path.length
        ? 'completed'
        : 'awaiting_decision'
      : 'running',
    scope: { startDate: '2026-07-14', endDate: '2026-07-28', accountIds: [], filters: {} },
    steps,
    automation: null,
    failure: null,
  };
}

// TODO: replace with useRunResult(step.runId) once the run comes from workflows.getSession
export function mockRunOf(actionType: string | null | undefined): WorkflowRun | null {
  return actionType === null || actionType === undefined
    ? null
    : (MOCK_STEP_RESULTS[actionType]?.run ?? null);
}
